import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFriends, Friendship } from "@/hooks/useFriends";
import { useNavigate } from "react-router-dom";
import { Zap, BookOpen, Lightbulb, Flame, Target, Clock, Swords } from "lucide-react";
import { cn } from "@/lib/utils";
import type { AnyValue } from "@/lib/rowTypes";

const COURSES = [
  "Financial Foundations", "Banking & Cash Management", "Credit & Debt Management",
  "Investing Fundamentals", "Insurance & Risk Protection", "Career & Income Strategy",
  "Family Finance", "Behavioral Finance", "Entrepreneurial Foundations",
];

const SCENARIO_TOPICS: { value: string; label: string }[] = [
  { value: "Budgeting", label: "Budgeting" },
  { value: "Saving", label: "Saving" },
  { value: "Credit", label: "Credit" },
  { value: "Debt", label: "Debt" },
  { value: "Banking", label: "Banking" },
  { value: "Investing", label: "Investing" },
  { value: "Career", label: "Career" },
  { value: "Lifestyle Inflation", label: "Lifestyle Inflation" },
];

const STREAK_METRICS = [
  { key: "lessons_completed", label: "Most lessons completed" },
  { key: "xp_gained", label: "Most XP earned" },
  { key: "challenge_wins", label: "Most challenge wins" },
];

const MILESTONE_METRICS = [
  { key: "lessons_completed", label: "Lessons completed", defaultTarget: 5 },
  { key: "xp_gained", label: "XP earned", defaultTarget: 500 },
];

type Mode = "menu" | "topic_battle" | "scenario_showdown" | "daily_duel" | "streak_war" | "milestone" | "series" | "quiz_duel";

export function NewChallengeModal({
  open, onOpenChange, friend,
}: { open: boolean; onOpenChange: (v: boolean) => void; friend: Friendship | null }) {
  const navigate = useNavigate();
  const { sendTopicChallenge, sendScenarioChallenge, sendDailyDuel, sendStreakWar, sendMilestoneChallenge, createSeries } = useFriends();
  const [mode, setMode] = useState<Mode>("menu");
  const [course, setCourse] = useState(COURSES[0]);
  const [scenarioTopic, setScenarioTopic] = useState<string>("");
  const [streakMetric, setStreakMetric] = useState(STREAK_METRICS[0].key);
  const [milestoneMetric, setMilestoneMetric] = useState(MILESTONE_METRICS[0].key);
  const [milestoneTarget, setMilestoneTarget] = useState(MILESTONE_METRICS[0].defaultTarget);
  const [seriesFormat, setSeriesFormat] = useState<3 | 5>(3);
  const [seriesType, setSeriesType] = useState<"topic_battle" | "scenario_showdown">("topic_battle");
  const [duration, setDuration] = useState(48);
  const [submitting, setSubmitting] = useState(false);

  const friendId = friend?.friend_profile?.id;
  const friendName = friend?.friend_profile?.display_name || "Friend";

  const close = () => { onOpenChange(false); setTimeout(() => setMode("menu"), 200); };

  const handle = async (fn: () => Promise<string | null>) => {
    if (!friendId) return;
    setSubmitting(true);
    const id = await fn();
    setSubmitting(false);
    if (id) {
      close();
      // Auto-route to active duel for instant gameplay if applicable
      if (mode === "topic_battle" || mode === "scenario_showdown" || mode === "daily_duel" || mode === "series") {
        navigate(`/challenge/${id}`);
      }
    }
  };

  const cards: { mode: Mode; icon: AnyValue; label: string; desc: string; tone: string }[] = [
    { mode: "topic_battle", icon: BookOpen, label: "Topic Battle", desc: "8 questions on a course you both know", tone: "text-primary" },
    { mode: "scenario_showdown", icon: Lightbulb, label: "Scenario Showdown", desc: "Real money decisions — pick the strongest move", tone: "text-accent" },
    { mode: "daily_duel", icon: Zap, label: "Daily Duel", desc: "5 quick questions • once a day", tone: "text-secondary" },
    { mode: "streak_war", icon: Flame, label: "Streak War", desc: "7-day consistency battle", tone: "text-orange-400" },
    { mode: "milestone", icon: Target, label: "Milestone Race", desc: "First to hit a target wins", tone: "text-emerald-400" },
    { mode: "series", icon: Swords, label: "Best-of-N Series", desc: "Multi-round rivalry — first to win majority", tone: "text-violet-400" },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(v); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === "menu" ? `Challenge ${friendName}` : "Configure Challenge"}</DialogTitle>
          <DialogDescription>
            {mode === "menu" ? "Pick a challenge type. Both players get the same content for fair competition." : "Set the rules and send."}
          </DialogDescription>
        </DialogHeader>

        {mode === "menu" && (
          <div className="grid gap-2">
            {cards.map(c => (
              <button
                key={c.mode}
                onClick={() => setMode(c.mode)}
                className="flex items-start gap-3 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-muted/30 transition text-left"
              >
                <c.icon className={cn("w-5 h-5 mt-0.5 shrink-0", c.tone)} />
                <div className="flex-1">
                  <p className="font-heading font-semibold text-sm text-foreground">{c.label}</p>
                  <p className="text-xs text-muted-foreground">{c.desc}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {mode === "topic_battle" && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Course</label>
              <Select value={course} onValueChange={setCourse}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{COURSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Time limit</label>
              <Select value={String(duration)} onValueChange={v => setDuration(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="24">24 hours</SelectItem>
                  <SelectItem value="48">48 hours</SelectItem>
                  <SelectItem value="72">3 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">8 questions, identical set for both players. Winner = most correct (time breaks ties).</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode("menu")} disabled={submitting}>Back</Button>
              <Button className="flex-1" disabled={submitting} onClick={() => handle(() => sendTopicChallenge(friendId!, course, 8, duration))}>
                Send Topic Battle
              </Button>
            </div>
          </div>
        )}

        {mode === "scenario_showdown" && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Topic (optional)</label>
              <Select value={scenarioTopic || "any"} onValueChange={v => setScenarioTopic(v === "any" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any topic (mixed)</SelectItem>
                  {SCENARIO_TOPICS.map(t => <SelectItem key={t.value} value={t.value} className="whitespace-normal">{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1">Choose a topic or leave it mixed for a variety of money decisions.</p>
            </div>
            <p className="text-xs text-muted-foreground">5 real-life money scenarios. Pick the strongest decision. Both players see the exact same scenarios. If a topic is still growing, we'll auto-build a mixed set.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode("menu")} disabled={submitting}>Back</Button>
              <Button className="flex-1" disabled={submitting} onClick={() => handle(() => sendScenarioChallenge(friendId!, scenarioTopic || null, 5, 48))}>
                Send Scenario Showdown
              </Button>
            </div>
          </div>
        )}

        {mode === "daily_duel" && (
          <div className="space-y-4">
            <p className="text-sm text-foreground">5 quick questions. You get one daily duel per day — make it count.</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> 24-hour time limit</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode("menu")} disabled={submitting}>Back</Button>
              <Button className="flex-1" disabled={submitting} onClick={() => handle(() => sendDailyDuel(friendId!))}>
                Send Daily Duel
              </Button>
            </div>
          </div>
        )}

        {mode === "streak_war" && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Compete on</label>
              <Select value={streakMetric} onValueChange={setStreakMetric}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STREAK_METRICS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">7-day consistency window. Highest total wins.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode("menu")} disabled={submitting}>Back</Button>
              <Button className="flex-1" disabled={submitting} onClick={() => handle(() => sendStreakWar(friendId!, streakMetric, 168))}>
                Start Streak War
              </Button>
            </div>
          </div>
        )}

        {mode === "milestone" && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Goal</label>
              <Select value={milestoneMetric} onValueChange={(v) => {
                setMilestoneMetric(v);
                const m = MILESTONE_METRICS.find(x => x.key === v);
                if (m) setMilestoneTarget(m.defaultTarget);
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MILESTONE_METRICS.map(m => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Target</label>
              <Select value={String(milestoneTarget)} onValueChange={v => setMilestoneTarget(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[3, 5, 10, 25, 50, 100, 250, 500, 1000].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">First to hit the target in 7 days wins.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode("menu")} disabled={submitting}>Back</Button>
              <Button className="flex-1" disabled={submitting} onClick={() => handle(() => sendMilestoneChallenge(friendId!, milestoneMetric, milestoneTarget, 168))}>
                Send Milestone
              </Button>
            </div>
          </div>
        )}
        {mode === "series" && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Series format</label>
              <Select value={String(seriesFormat)} onValueChange={v => setSeriesFormat(Number(v) as 3 | 5)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Best of 3 (first to 2 wins)</SelectItem>
                  <SelectItem value="5">Best of 5 (first to 3 wins)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Round type</label>
              <Select value={seriesType} onValueChange={v => setSeriesType(v as AnyValue)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="topic_battle">Topic Battle (8 questions)</SelectItem>
                  <SelectItem value="scenario_showdown">Scenario Showdown (5 decisions)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {seriesType === "topic_battle" && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Course</label>
                <Select value={course} onValueChange={setCourse}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{COURSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {seriesType === "scenario_showdown" && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Topic (optional)</label>
                <Select value={scenarioTopic || "any"} onValueChange={v => setScenarioTopic(v === "any" ? "" : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any topic</SelectItem>
                    {SCENARIO_TOPICS.map(t => <SelectItem key={t.value} value={t.value} className="whitespace-normal">{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <p className="text-xs text-muted-foreground">First round starts now. After each round completes, send the next via the Active tab.</p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setMode("menu")} disabled={submitting}>Back</Button>
              <Button className="flex-1" disabled={submitting} onClick={() => handle(() =>
                createSeries(friendId!, seriesFormat, seriesType,
                  seriesType === "topic_battle" ? course : undefined,
                  seriesType === "scenario_showdown" ? (scenarioTopic || undefined) : undefined)
              )}>
                Start Series + Round 1
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
